/**
 * Brand voice analysis client — AI-09 §C2. The Idempotency-Key is a
 * content hash of (kit, emails): clicking Analyze twice on the same
 * input replays the cached result server-side instead of burning a
 * second sonnet call.
 */
import { apiClient } from "./apiClient";
import { patchBrandKit, type BrandKit, type BrandKitVoice } from "./brandKits";

export interface VoiceSuggestion {
  tone: string;
  personality_traits: string[];
  language_level: string;
  signature_phrases: string[];
  banned_words_detected: string[];
  tone_description: string;
}

export interface VoiceAnalysis {
  suggestion: VoiceSuggestion;
  confidence: number;
  metadata: {
    model?: string;
    tokens_used?: number;
    duration_ms?: number;
    truncated?: boolean;
  };
}

export async function analyzeVoice(
  kitId: string,
  emails: string[],
  language?: string,
): Promise<VoiceAnalysis> {
  const key = await idempotencyKeyFor(kitId, emails);
  const res = await apiClient.post<{ data: VoiceAnalysis }>(
    `/v1/brand-kits/${kitId}/analyze-voice`,
    { emails, ...(language !== undefined && language !== "" ? { language } : {}) },
    { headers: { "Idempotency-Key": key } },
  );
  return res.data;
}

/** Persist via the existing RFC 7396 PATCH — `null` deletes the profile. */
export async function saveVoice(
  kitId: string,
  voice: BrandKitVoice | null,
): Promise<BrandKit> {
  return patchBrandKit(kitId, { data: { voice } });
}

async function idempotencyKeyFor(kitId: string, emails: string[]): Promise<string> {
  const input = new TextEncoder().encode(kitId + JSON.stringify(emails));
  const digest = await crypto.subtle.digest("SHA-256", input);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
